import type { ReactNode } from "react";
import {
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { Link, useLocation } from "react-router-dom";

import {
    ChevronLeftIcon,
    DocumentIcon,
    LogoutIcon,
    MailIcon,
    UserIcon,
    UsersIcon,
} from "@/assets/icons";
import { Button, Logo } from "@/atoms";
import { LogoutModal } from "@/components";
import { routes, strings } from "@/constants";
import { LogoSizeEnum, RoleEnum } from "@/enums";
import { useAuth } from "@/hooks";
import type { ProfileNavItemInterface } from "@/interfaces";

const lineColor = "#0025BA";

const lineGradient = `linear-gradient(to bottom, transparent 0%, ${lineColor} 30%, ${lineColor} 70%, transparent 100%)`;

const buildUserNavHandler = (): ProfileNavItemInterface[] => [
    {
        icon: <UserIcon className="h-5 w-5" />,
        label: strings.profileNav.profile,
        to: routes.profile,
    },
];

const buildAdminNavHandler = (): ProfileNavItemInterface[] => [
    {
        icon: <UserIcon className="h-5 w-5" />,
        label: strings.profileNav.profile,
        to: routes.profile,
    },
    {
        icon: <UsersIcon className="h-5 w-5" />,
        label: strings.profileNav.companies,
        to: routes.adminCompanies,
    },
    {
        icon: <DocumentIcon className="h-5 w-5" />,
        label: strings.profileNav.invitations,
        to: routes.adminInvitations,
    },
    {
        icon: <MailIcon className="h-5 w-5" />,
        label: strings.profileNav.sendInvite,
        to: routes.adminSendInvite,
    },
];

export const ProfileLayout = ({ children }: { children: ReactNode }) => {
    const navItemRefs = useRef<Array<HTMLAnchorElement | null>>([]);

    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

    const [indicator, setIndicator] = useState({
        isReady: false,
        left: 0,
        width: 0,
    });

    const [isAnimated, setIsAnimated] = useState(false);

    const location = useLocation();

    const {
        onLogout,
        user,
    } = useAuth();

    const isAdmin = user?.role === RoleEnum.ADMIN;

    const navItems = isAdmin ? buildAdminNavHandler() : buildUserNavHandler();

    const isActiveHandler = (path: string): boolean => {
        if (path === routes.adminCompanies) return location.pathname.startsWith(routes.adminCompanies);

        return location.pathname === path;
    };

    const activeIndex = navItems.findIndex(({ to }) => isActiveHandler(to));

    useLayoutEffect(
        () => {
            const activeEl = navItemRefs.current[activeIndex];

            if (!activeEl) {
                setIndicator((prev) => ({
                    ...prev,
                    isReady: false,
                }));

                return;
            }

            setIndicator({
                isReady: true,
                left: activeEl.offsetLeft,
                width: activeEl.offsetWidth,
            });

            const frameId = requestAnimationFrame(() => setIsAnimated(true));

            return () => cancelAnimationFrame(frameId);
        },
        [
            activeIndex,
            location.pathname,
        ],
    );

    useEffect(
        () => {
            const handleResizeHandler = () => {
                const activeEl = navItemRefs.current[activeIndex];

                if (!activeEl) return;

                setIndicator({
                    isReady: true,
                    left: activeEl.offsetLeft,
                    width: activeEl.offsetWidth,
                });
            };

            window.addEventListener(
                "resize",
                handleResizeHandler,
            );

            return () => window.removeEventListener(
                "resize",
                handleResizeHandler,
            );
        },
        [activeIndex],
    );

    const openLogoutModalHandler = () => setIsLogoutModalOpen(true);

    const closeLogoutModalHandler = () => setIsLogoutModalOpen(false);

    const logoutHandler = async () => {
        setIsLoggingOut(true);

        try {
            await onLogout();
        } catch (error) {
            console.error(
                strings.debug.logoutFailed,
                error,
            );
        } finally {
            setIsLoggingOut(false);

            setIsLogoutModalOpen(false);
        }
    };

    return (
        <div
            className="
                relative
                min-h-screen
                overflow-hidden
                bg-background
                md:h-screen
                md:overflow-hidden
            "
        >
            <div
                aria-hidden
                className="
                    pointer-events-none
                    absolute
                    -top-40
                    -left-40
                    h-[30rem]
                    w-[30rem]
                    rounded-full
                    bg-primary/30
                    blur-[110px]
                "
            />
            <div
                aria-hidden
                className="
                    pointer-events-none
                    absolute
                    top-1/4
                    -right-32
                    h-[28rem]
                    w-[28rem]
                    rounded-full
                    bg-primary-light/35
                    blur-[110px]
                "
            />
            <div
                aria-hidden
                className="
                    pointer-events-none
                    absolute
                    -bottom-40
                    left-1/3
                    h-[26rem]
                    w-[26rem]
                    rounded-full
                    bg-secondary/25
                    blur-[110px]
                "
            />
            <div
                className="
                    relative
                    mx-2
                    flex
                    min-h-screen
                    flex-col
                    md:mx-12
                    md:h-full
                "
            >
                <div
                    style={{ background: lineGradient }}
                    className="
                        absolute
                        top-0
                        bottom-0
                        left-0
                        hidden
                        w-0.5
                        md:block
                    "
                />
                <div
                    style={{ background: lineGradient }}
                    className="
                        absolute
                        top-0
                        right-0
                        bottom-0
                        hidden
                        w-0.5
                        md:block
                    "
                />
                <header
                    className="
                        flex
                        shrink-0
                        items-center
                        justify-between
                        gap-3
                        px-4
                        py-3
                        md:px-6
                        md:py-4
                    "
                >
                    <div className="flex items-center gap-3 md:gap-4">
                        <Link to={routes.code}>
                            <Logo size={LogoSizeEnum.MD} />
                        </Link>
                        <Link
                            to={routes.code}
                            style={{
                                backdropFilter: "blur(20px) saturate(180%)",
                                boxShadow: "0 3px 14px rgba(19,86,188,0.08), inset 0 1px 0 rgba(255,255,255,0.7)",
                                WebkitBackdropFilter: "blur(20px) saturate(180%)",
                            }}
                            className="
                                hidden
                                items-center
                                gap-1.5
                                rounded-full
                                border
                                border-white/55
                                bg-white/50
                                px-3
                                py-1.5
                                text-xs
                                font-medium
                                text-text-secondary
                                transition-all
                                duration-200
                                hover:border-primary/40
                                hover:bg-white/70
                                hover:text-primary
                                sm:flex
                                md:px-4
                                md:py-2
                                md:text-sm
                            "
                        >
                            <ChevronLeftIcon className="h-4 w-4" />
                            <span>{strings.profile.backToCode}</span>
                        </Link>
                    </div>
                    <div className="flex items-center gap-2">
                        <div
                            className="
                                hidden
                                items-center
                                gap-2
                                rounded-lg
                                px-3
                                py-2
                                text-primary
                                md:flex
                            "
                        >
                            <UserIcon className="h-5 w-5" />
                            <span className="text-sm font-medium">{user?.name || strings.code.defaultUserName}</span>
                            {isAdmin && (
                                <span
                                    className="
                                        ml-1
                                        rounded-full
                                        bg-primary/10
                                        px-2
                                        py-0.5
                                        text-xs
                                        font-semibold
                                        tracking-wide
                                        text-primary
                                        uppercase
                                    "
                                >
                                    {strings.profile.roleAdmin}
                                </span>
                            )}
                        </div>
                        <Button
                            disabled={isLoggingOut}
                            variant="ghost-danger"
                            className="
                                flex
                                items-center
                                gap-2
                                md:hidden
                            "
                            onClick={openLogoutModalHandler}
                        >
                            <LogoutIcon className="h-5 w-5" />
                        </Button>
                    </div>
                </header>
                <div
                    className="
                        relative
                        z-10
                        flex
                        min-h-0
                        flex-1
                        flex-col
                        gap-4
                        px-2
                        pb-4
                        md:gap-6
                        md:px-6
                        md:pb-6
                    "
                >
                    <nav
                        style={{
                            backdropFilter: "blur(24px) saturate(180%)",
                            boxShadow: "0 6px 24px rgba(19,86,188,0.10), inset 0 1px 0 rgba(255,255,255,0.7)",
                            WebkitBackdropFilter: "blur(24px) saturate(180%)",
                        }}
                        className="
                            relative
                            flex
                            shrink-0
                            items-center
                            gap-1
                            overflow-x-auto
                            rounded-full
                            border
                            border-white/55
                            bg-white/50
                            p-1.5
                            md:gap-2
                            md:p-2
                        "
                    >
                        <span
                            aria-hidden
                            style={{
                                boxShadow: "0 0 24px rgba(19,86,188,0.55), 0 4px 14px rgba(0,37,186,0.35)",
                                opacity: indicator.isReady ? 1 : 0,
                                transform: `translateX(${indicator.left}px)`,
                                transition: isAnimated
                                    ? "transform 450ms cubic-bezier(0.16, 1, 0.3, 1), width 450ms cubic-bezier(0.16, 1, 0.3, 1), opacity 200ms ease"
                                    : "opacity 200ms ease",
                                width: `${indicator.width}px`,
                                willChange: "transform, width",
                            }}
                            className="
                                pointer-events-none
                                absolute
                                top-1.5
                                bottom-1.5
                                left-0
                                rounded-full
                                bg-gradient-to-br
                                from-primary-light
                                to-primary-dark
                                md:top-2
                                md:bottom-2
                            "
                        />
                        {navItems.map((
                            {
                                icon,
                                label,
                                to,
                            },
                            index,
                        ) => (
                            <Link
                                key={to}
                                ref={(el) => {
                                    navItemRefs.current[index] = el;
                                }}
                                to={to}
                                className={`
                                    relative
                                    z-10
                                    flex
                                    shrink-0
                                    items-center
                                    gap-2
                                    rounded-full
                                    px-4
                                    py-2
                                    text-sm
                                    font-medium
                                    whitespace-nowrap
                                    transition-colors
                                    duration-200
                                    md:px-5
                                    md:py-2.5
                                    ${isActiveHandler(to) ? "text-white" : "text-text-secondary hover:text-primary"}
                                `}
                            >
                                {icon}
                                <span>{label}</span>
                            </Link>
                        ))}
                    </nav>
                    <main
                        className="
                            min-h-0
                            min-w-0
                            flex-1
                            md:overflow-y-auto
                        "
                    >
                        {children}
                    </main>
                </div>
                <footer
                    className="
                        hidden
                        shrink-0
                        px-6
                        py-3
                        md:block
                    "
                >
                    <Button
                        className="flex items-center gap-2"
                        disabled={isLoggingOut}
                        variant="ghost-danger"
                        onClick={openLogoutModalHandler}
                    >
                        <LogoutIcon className="h-5 w-5" />
                        {isLoggingOut ? strings.common.loggingOut : strings.common.logout}
                    </Button>
                </footer>
            </div>
            <LogoutModal
                isLoading={isLoggingOut}
                isOpen={isLogoutModalOpen}
                onClose={closeLogoutModalHandler}
                onConfirm={logoutHandler}
            />
        </div>
    );
};
